// Hire AppShell - top-bar chrome ported from AIMS (Direction C · Soft Panel).
//   Top AppBar on all sizes: smart Back, logo (-> home), right cluster
//   (Home, theme toggle, menu drawer with full nav + account). Mobile also keeps
//   a bottom nav for the primary sections.
// Renders chrome only when logged in AND not on the wallboard (a fullscreen wall
// display gets no app chrome); otherwise renders children bare (login / board).

import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import LightModeIcon from '@mui/icons-material/LightMode';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { ViewInAr, AddCircle } from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LockResetIcon from '@mui/icons-material/LockReset';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';

import { useColorMode } from '../theme/ThemeContext';
import { baseUrl } from '../shared';
import logo from '../pages/logo.png';
import ResetPassword from '../pages/resetPassword';
import Create from '../pages/create/createItem';

const can = (flag) => localStorage.getItem(flag) === '1';
const clean = (v) => (v ? String(v).replace(/"/g, '') : '');

// Permission-filtered nav (drawer + mobile bottom nav). `primary` -> bottom nav.
// Hire gates everything on adminFunctions; wallboard also allows canViewWallboard.
function buildNav() {
  const admin = can('adminFunctions');
  return [
    { label: 'Item View', to: '/', icon: <ViewInAr />, show: admin, primary: true },
    { label: 'Create Item', to: '/createItem', icon: <AddCircle />, show: admin },
    { label: 'Open Bookings', to: '/openBookings', icon: <CalendarMonthIcon />, show: admin, primary: true },
    { label: 'Admin', to: '/admin', icon: <AdminPanelSettingsIcon />, show: admin },
    { label: 'Wallboard', to: '/wallboard', icon: <DashboardIcon />, show: admin || can('canViewWallboard'), primary: true },
  ].filter((i) => i.show);
}

function BrandLogo({ size = 34 }) {
  return (
    <Box component="img" src={logo} alt="1 Maintenance & Supply Hire" sx={{ height: size, width: 'auto', display: 'block', borderRadius: '8px' }} />
  );
}

function isActive(item, pathname) {
  if (item.to === '/') return pathname === '/';
  return pathname === item.to || pathname.startsWith(item.to + '/');
}

function DrawerRow({ item, active, onClick }) {
  const asButton = !item.to && item.onClick;
  const handleClick = (e) => { if (item.onClick) item.onClick(e); if (onClick) onClick(e); };
  return (
    <Box
      component={asButton ? 'button' : Link}
      to={asButton ? undefined : item.to}
      onClick={handleClick}
      sx={{
        ...(asButton ? { border: 'none', background: 'transparent', width: 'calc(100% - 16px)', textAlign: 'left', cursor: 'pointer', font: 'inherit' } : {}),
        display: 'flex', alignItems: 'center', gap: 1.4, textDecoration: 'none',
        px: 1.5, py: 1.1, mx: 1, borderRadius: '10px', fontSize: 14,
        fontWeight: active ? 600 : 500,
        color: active ? 'var(--primary)' : 'var(--text)',
        background: active ? 'var(--primary-tint)' : 'transparent',
        '& svg': { fontSize: 20 },
        '&:hover': { background: 'var(--surface-2)' },
      }}
    >
      {item.icon}
      <span>{item.label}</span>
    </Box>
  );
}

function GroupLabel({ children }) {
  return (
    <Box sx={{ px: 2.5, pt: 1.6, pb: 0.5, fontSize: 10, fontWeight: 600, letterSpacing: '.7px', textTransform: 'uppercase', color: 'var(--faint)' }}>
      {children}
    </Box>
  );
}

function NavList({ nav, pathname, onNavigate }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
      {nav.map((item) => (
        <DrawerRow key={item.to || item.label} item={item} active={item.to ? isActive(item, pathname) : false} onClick={onNavigate} />
      ))}
    </Box>
  );
}

export default function AppShell({ children }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();
  const { mode, toggle } = useColorMode();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const loggedIn = localStorage.getItem('username') !== null;
  // Wallboard-only users (can view the board but have no admin) get the
  // fullscreen wall display with NO app chrome, on every route. Users WITH admin
  // keep the chrome even on the wallboard, so they can navigate away from it.
  const wallboardOnly = can('canViewWallboard') && !can('adminFunctions');

  // Create Item opens as a modal overlaying the current page (not a route nav),
  // so it appears on top of wherever you are.
  const nav = useMemo(() => {
    if (!loggedIn) return [];
    return buildNav().map((i) =>
      i.to === '/createItem'
        ? { label: i.label, icon: i.icon, onClick: () => { setCreateItemOpen(true); setDrawerOpen(false); } }
        : i
    );
  }, [loggedIn]);

  // Login flow + wallboard-only (kiosk) users render bare (no chrome).
  if (!loggedIn || wallboardOnly) return <>{children}</>;

  const name = clean(localStorage.getItem('fullName'));
  const isHome = location.pathname === '/';
  // The wallboard is a full-bleed dark display: an admin viewing it keeps the top
  // bar, but the content area drops its padding so the board fills the space.
  const isWallboard = location.pathname === '/wallboard';

  const handleBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) { navigate(-1); return; }
    navigate('/');
  };

  const accountActions = [
    { label: 'Reset Password', icon: <LockResetIcon />, onClick: () => { setResetOpen(true); setDrawerOpen(false); } },
    { label: 'Logout', icon: <MeetingRoomIcon />, onClick: () => { setLogoutOpen(true); setDrawerOpen(false); } },
  ];

  const doLogout = () => {
    setLoggingOut(true);
    fetch(baseUrl + 'user/logout', {
      method: 'POST',
      headers: { username: localStorage.getItem('username'), bToken: localStorage.getItem('bToken') },
    })
      .then((r) => r.json())
      .catch(() => {})
      .finally(() => { localStorage.clear(); window.location.href = '/'; });
  };

  const primaries = nav.filter((i) => i.primary);
  const activePrimary = primaries.find((i) => isActive(i, location.pathname));
  const bottomValue = activePrimary ? activePrimary.to : '__more';
  const handleBottom = (_e, value) => {
    if (value === '__more') { setDrawerOpen(true); return; }
    navigate(value);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <AppBar position="sticky" component="div" elevation={0} sx={{ background: 'var(--rail)', color: 'var(--on-rail)' }}>
        <Toolbar sx={{ gap: 0.5 }}>
          {!isHome && (
            <Button onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ color: 'var(--on-rail)', minWidth: 0, mr: 0.5 }}>
              {isMobile ? '' : 'Back'}
            </Button>
          )}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit', flexGrow: 1, minWidth: 0 }}>
            <BrandLogo size={38} />
          </Box>
          {!isHome && (
            <Tooltip title="Home">
              <IconButton component={Link} to="/" sx={{ color: 'var(--on-rail)' }}><HomeOutlinedIcon /></IconButton>
            </Tooltip>
          )}
          <Tooltip title={mode === 'light' ? 'Dark mode' : 'Light mode'}>
            <IconButton onClick={toggle} sx={{ color: 'var(--on-rail)' }}>{mode === 'light' ? <BedtimeIcon /> : <LightModeIcon />}</IconButton>
          </Tooltip>
          {!isMobile && (
            <Tooltip title="Menu">
              <IconButton edge="end" onClick={() => setDrawerOpen(true)} sx={{ color: 'var(--on-rail)' }}><MenuIcon /></IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>

      <Box component="main" sx={isWallboard ? { p: 0 } : { p: { xs: 2, md: 3 }, pb: { xs: '84px', sm: 3 } }}>
        {children}
      </Box>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={isMobile ? { sx: { borderTopLeftRadius: 'var(--r)', borderTopRightRadius: 'var(--r)', maxHeight: '75vh' } } : undefined}
      >
        <Box sx={{ width: isMobile ? 'auto' : 268, py: 2, pb: isMobile ? '84px' : 2, background: 'var(--surface)', height: isMobile ? 'auto' : '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, pb: 1.5, mb: 0.5, borderBottom: '1px solid var(--border)' }}>
            <Box sx={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{name || 'Signed in'}</Box>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <NavList
              nav={[{ label: 'Home', to: '/', icon: <HomeOutlinedIcon /> }, ...nav.filter((i) => i.to !== '/')]}
              pathname={location.pathname}
              onNavigate={() => setDrawerOpen(false)}
            />
            <GroupLabel>Account</GroupLabel>
            <NavList nav={accountActions} pathname={location.pathname} onNavigate={() => setDrawerOpen(false)} />
          </Box>
        </Box>
      </Drawer>

      {isMobile && primaries.length > 0 && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100, borderTop: '1px solid var(--border)' }} elevation={3}>
          <BottomNavigation value={bottomValue} onChange={handleBottom} showLabels>
            {primaries.map((i) => (
              <BottomNavigationAction key={i.to} label={i.label} value={i.to} icon={i.icon} />
            ))}
            <BottomNavigationAction label="More" value="__more" icon={<MoreHorizIcon />} />
          </BottomNavigation>
        </Paper>
      )}

      <Dialog open={logoutOpen} onClose={() => setLogoutOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Log out?</DialogTitle>
        <DialogContent>
          <DialogContentText>You will be returned to the login screen.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutOpen(false)} disabled={loggingOut}>Cancel</Button>
          <Button onClick={doLogout} variant="contained" color="error" disabled={loggingOut}>{loggingOut ? 'Logging out...' : 'Log out'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent dividers><ResetPassword /></DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button type="submit" form="resetMyPasswordForm" variant="contained">Reset Password</Button>
        </DialogActions>
      </Dialog>

      {/* Create Item overlays the current page (Create renders its own Dialog). */}
      {createItemOpen && <Create onClose={() => setCreateItemOpen(false)} />}
    </Box>
  );
}
